import React, { useState, useEffect, useCallback, useRef } from "react";
import { CATEGORIES, COUNTRIES, DISTINCTIONS, ACCOLADES, avgScore, CRITERIA } from "../mock";
import { adminLogin, adminFetchAll, adminCreate, adminUpdate, adminToggle, adminDelete, fetchSignups, reorderPlaces, adminFetchGuides, adminCreateGuide, adminUpdateGuide, adminDeleteGuide } from "../api";
import { usePlaces } from "../context/PlacesContext";
import { Plus, Pencil, Trash2, Lock, X, LogOut, Eye, EyeOff, Download, GripVertical } from "lucide-react";

const TOKEN_KEY = "pw_admin_token";

const EMPTY = {
  name: "", category: "Restaurant", country: "Canada", region: "Alberta", city: "Calgary", neighborhood: "",
  cuisine: "", price: "$$", image: "", excerpt: "", featured: false, visible: true, distinction: 2, accolades: [],
  scores: { food: 8, atmosphere: 8, service: 8, value: 8, experience: 8 },
};

const EMPTY_GUIDE = {
  title: "", slug: "", cover: "", dek: "", author: "The Placeworthy Editors", date: "2025", placeSlugs: [], visible: true,
};

export default function Admin() {
  const { reload } = usePlaces();
  const [token, setToken] = useState(sessionStorage.getItem(TOKEN_KEY) || "");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("places");
  const [places, setPlaces] = useState([]);
  const [guides, setGuides] = useState([]);
  const [signups, setSignups] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingGuide, setEditingGuide] = useState(null);
  const [showGuideForm, setShowGuideForm] = useState(false);
  const dragItem = useRef(null);
  const dragOver = useRef(null);

  const handleReorder = () => {
    const from = dragItem.current, to = dragOver.current;
    dragItem.current = null; dragOver.current = null;
    if (from == null || to == null || from === to) return;
    const next = [...places];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setPlaces(next);
    reorderPlaces(token, next.map((p) => p.id)).then(reload).catch(() => {});
  };

  const refresh = useCallback((tok) => {
    setBusy(true);
    adminFetchAll(tok).then(setPlaces).catch(() => setPlaces([])).finally(() => setBusy(false));
    fetchSignups(tok).then(setSignups).catch(() => setSignups([]));
    adminFetchGuides(tok).then(setGuides).catch(() => setGuides([]));
  }, []);

  useEffect(() => { if (token) refresh(token); }, [token, refresh]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { token: tok } = await adminLogin(pw);
      sessionStorage.setItem(TOKEN_KEY, tok);
      setToken(tok);
    } catch {
      setErr("Incorrect passcode.");
    }
  };

  const logout = () => { sessionStorage.removeItem(TOKEN_KEY); setToken(""); setPlaces([]); setSignups([]); setGuides([]); };

  const exportSignups = () => {
    const rows = [["email", "source", "date"], ...signups.map((s) => [s.email, s.source, new Date(s.createdAt).toISOString()])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "placeworthy-early-access.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const openNew = () => { setEditing({ ...EMPTY }); setShowForm(true); };
  const openEdit = (p) => { setEditing(JSON.parse(JSON.stringify(p))); setShowForm(true); };

  const remove = async (id) => {
    await adminDelete(token, id);
    refresh(token); reload();
  };

  const toggleVisible = async (p) => {
    const updated = await adminToggle(token, p.id, !p.visible);
    setPlaces((prev) => prev.map((x) => (x.id === p.id ? updated : x)));
    reload();
  };

  const save = async () => {
    const payload = { ...editing };
    if (editing.id) {
      await adminUpdate(token, editing.id, payload);
    } else {
      await adminCreate(token, payload);
    }
    setShowForm(false); setEditing(null);
    refresh(token); reload();
  };

  const openNewGuide = () => { setEditingGuide({ ...EMPTY_GUIDE }); setShowGuideForm(true); };
  const openEditGuide = (g) => { setEditingGuide(JSON.parse(JSON.stringify(g))); setShowGuideForm(true); };

  const saveGuide = async () => {
    const payload = { ...editingGuide };
    if (editingGuide.id) {
      await adminUpdateGuide(token, editingGuide.id, payload);
    } else {
      await adminCreateGuide(token, payload);
    }
    setShowGuideForm(false); setEditingGuide(null);
    refresh(token); reload();
  };

  const removeGuide = async (id) => {
    if (!window.confirm("Delete this guide? This cannot be undone.")) return;
    await adminDeleteGuide(token, id);
    refresh(token); reload();
  };

  const toggleGuideVisible = async (g) => {
    const updated = await adminUpdateGuide(token, g.id, { visible: !g.visible });
    setGuides((prev) => prev.map((x) => (x.id === g.id ? updated : x)));
    reload();
  };

  const togglePlaceInGuide = (slug) => {
    setEditingGuide((prev) => {
      const has = (prev.placeSlugs || []).includes(slug);
      return { ...prev, placeSlugs: has ? prev.placeSlugs.filter((s) => s !== slug) : [...(prev.placeSlugs || []), slug] };
    });
  };

  if (!token) {
    return (
      <div className="pt-[68px] min-h-screen flex items-center justify-center bg-ink text-paper relative grain">
        <form onSubmit={login} className="relative bg-paper text-ink p-10 w-full max-w-sm mx-6">
          <Lock className="w-7 h-7 text-claret mb-4" />
          <h1 className="font-serif text-3xl font-semibold">Editor Login</h1>
          <p className="text-ink-soft text-sm mt-2">Placeworthy content studio — internal team only.</p>
          <input type="password" value={pw} onChange={(e) => { setPw(e.target.value); setErr(""); }} placeholder="Passcode" className="admin-input mt-6" />
          {err && <p className="text-claret text-sm mt-2">{err}</p>}
          <button className="w-full mt-4 bg-claret text-paper py-3 font-semibold tracking-wide hover:bg-claret-dark transition-colors">Enter Studio</button>
        </form>
      </div>
    );
  }

  const visibleCount = places.filter((p) => p.visible).length;
  const visibleGuides = guides.filter((g) => g.visible).length;

  return (
    <div className="pt-[68px] min-h-screen bg-paper-dark">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <span className="eyebrow text-claret text-[10px]">Content Studio</span>
            <h1 className="font-serif text-4xl font-semibold">{tab === "places" ? "Manage Places" : "Manage Guides"}</h1>
            {tab === "places"
              ? <p className="text-ink-soft text-sm mt-1">{places.length} places · {visibleCount} visible to visitors · saved to the database.</p>
              : <p className="text-ink-soft text-sm mt-1">{guides.length} guides · {visibleGuides} visible to visitors · saved to the database.</p>}
          </div>
          <div className="flex items-center gap-3">
            {tab === "places"
              ? <button onClick={openNew} data-testid="add-place-btn" className="inline-flex items-center gap-2 bg-claret text-paper px-5 py-3 text-sm font-semibold hover:bg-claret-dark transition-colors"><Plus className="w-4 h-4" /> Add Place</button>
              : <button onClick={openNewGuide} data-testid="add-guide-btn" className="inline-flex items-center gap-2 bg-claret text-paper px-5 py-3 text-sm font-semibold hover:bg-claret-dark transition-colors"><Plus className="w-4 h-4" /> Add Guide</button>}
            <button onClick={logout} className="inline-flex items-center gap-2 border border-ink/20 px-4 py-3 text-sm font-semibold hover:bg-ink/5 transition-colors"><LogOut className="w-4 h-4" /> Log out</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mt-8 border-b border-ink/10">
          <button
            onClick={() => setTab("places")}
            data-testid="tab-places"
            className={`px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === "places" ? "border-claret text-claret" : "border-transparent text-ink-soft hover:text-ink"}`}
          >
            Places ({places.length})
          </button>
          <button
            onClick={() => setTab("guides")}
            data-testid="tab-guides"
            className={`px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === "guides" ? "border-claret text-claret" : "border-transparent text-ink-soft hover:text-ink"}`}
          >
            Guides ({guides.length})
          </button>
        </div>

        {tab === "places" && (
        <>
        {/* Early access signups */}
        <div className="mt-8 bg-paper border border-ink/10 p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <span className="eyebrow text-claret text-[10px]">Early access</span>
              <h2 className="font-serif text-2xl font-semibold">{signups.length} email signup{signups.length === 1 ? "" : "s"}</h2>
              <p className="text-ink-soft text-sm mt-1">Collected from the footer and Videos forms across the site.</p>
            </div>
            {signups.length > 0 && (
              <button onClick={exportSignups} className="inline-flex items-center gap-2 border border-ink/20 px-4 py-3 text-sm font-semibold hover:bg-ink/5 transition-colors">
                <Download className="w-4 h-4" /> Export CSV
              </button>
            )}
          </div>
          {signups.length > 0 && (
            <div className="mt-5 max-h-56 overflow-y-auto border-t border-ink/10">
              {signups.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-4 py-2.5 border-b border-ink/10 last:border-0 text-sm">
                  <span className="font-medium truncate">{s.email}</span>
                  <span className="text-ink-soft text-xs shrink-0">{s.source} · {new Date(s.createdAt).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-10 bg-paper border border-ink/10">
          <div className="hidden md:grid grid-cols-[64px_1fr_130px_150px_90px_160px] gap-4 px-5 py-3 border-b border-ink/10 eyebrow text-ink-soft text-[10px]">
            <span>Photo</span><span>Name</span><span>Category</span><span>Location</span><span>Marks</span><span className="text-right">Visibility & actions</span>
          </div>
          {busy ? (
            <p className="px-5 py-8 text-ink-soft">Loading…</p>
          ) : places.map((p, idx) => (
            <div
              key={p.id}
              draggable
              onDragStart={() => { dragItem.current = idx; }}
              onDragEnter={() => { dragOver.current = idx; }}
              onDragEnd={handleReorder}
              onDragOver={(e) => e.preventDefault()}
              className={`grid grid-cols-[64px_1fr] md:grid-cols-[64px_1fr_130px_150px_90px_160px] gap-4 items-center px-5 py-4 border-b border-ink/10 last:border-0 cursor-move hover:bg-ink/[0.02] ${!p.visible ? "opacity-55" : ""}`}
            >
              <div className="w-14 h-14 bg-ink/5 overflow-hidden relative">
                <span className="absolute -left-1 top-1/2 -translate-y-1/2 text-ink-soft/50 hidden md:block"><GripVertical className="w-4 h-4" /></span>
                {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover" /> : null}
              </div>
              <div className="min-w-0">
                <p className="font-serif text-lg font-semibold truncate">{p.name}</p>
                <p className="text-xs text-ink-soft truncate">{p.cuisine}</p>
              </div>
              <span className="hidden md:block text-sm text-ink-soft">{p.category}</span>
              <span className="hidden md:block text-sm text-ink-soft truncate">{p.city}, {p.country}</span>
              <span className="hidden md:flex items-center gap-0.5">
                {p.distinction > 0 ? Array.from({ length: p.distinction }).map((_, i) => (
                  <img key={i} src="/placeworthy-mark.png" alt="" aria-hidden="true" className="w-5 h-5" />
                )) : <span className="text-xs text-ink-soft/60">—</span>}
              </span>
              <div className="hidden md:flex items-center gap-2 justify-end">
                <button onClick={() => toggleVisible(p)} title={p.visible ? "Visible — click to hide" : "Hidden — click to show"} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${p.visible ? "bg-ink/5 text-ink hover:bg-ink/10" : "bg-brass/20 text-ink hover:bg-brass/30"}`}>
                  {p.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  {p.visible ? "Live" : "Hidden"}
                </button>
                <button onClick={() => openEdit(p)} className="p-2 hover:bg-ink/5 text-ink-soft hover:text-claret transition-colors" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(p.id)} className="p-2 hover:bg-ink/5 text-ink-soft hover:text-claret transition-colors" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
        </>
        )}

        {tab === "guides" && (
          <div className="mt-8 bg-paper border border-ink/10" data-testid="guides-panel">
            <div className="hidden md:grid grid-cols-[80px_1fr_110px_140px] gap-4 px-5 py-3 border-b border-ink/10 eyebrow text-ink-soft text-[10px]">
              <span>Cover</span><span>Guide</span><span>Places</span><span className="text-right">Visibility & actions</span>
            </div>
            {busy ? (
              <p className="px-5 py-8 text-ink-soft">Loading…</p>
            ) : guides.length === 0 ? (
              <p className="px-5 py-8 text-ink-soft">No guides yet. Click “Add Guide” to create your first curated list.</p>
            ) : guides.map((g) => (
              <div key={g.id} data-testid={`guide-row-${g.slug}`} className={`grid grid-cols-[80px_1fr] md:grid-cols-[80px_1fr_110px_140px] gap-4 items-center px-5 py-4 border-b border-ink/10 last:border-0 ${!g.visible ? "opacity-55" : ""}`}>
                <div className="w-16 h-16 bg-ink/5 overflow-hidden">
                  {g.cover ? <img src={g.cover} alt={g.title} className="w-full h-full object-cover" /> : null}
                </div>
                <div className="min-w-0">
                  <p className="font-serif text-lg font-semibold truncate">{g.title}</p>
                  <p className="text-xs text-ink-soft truncate">/guide/{g.slug} · {g.author}</p>
                </div>
                <span className="hidden md:block text-sm text-ink-soft">{(g.placeSlugs || []).length} places</span>
                <div className="hidden md:flex items-center gap-2 justify-end">
                  <button onClick={() => toggleGuideVisible(g)} title={g.visible ? "Visible — click to hide" : "Hidden — click to show"} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${g.visible ? "bg-ink/5 text-ink hover:bg-ink/10" : "bg-brass/20 text-ink hover:bg-brass/30"}`}>
                    {g.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    {g.visible ? "Live" : "Hidden"}
                  </button>
                  <button onClick={() => openEditGuide(g)} data-testid={`edit-guide-${g.slug}`} className="p-2 hover:bg-ink/5 text-ink-soft hover:text-claret transition-colors" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => removeGuide(g.id)} data-testid={`delete-guide-${g.slug}`} className="p-2 hover:bg-ink/5 text-ink-soft hover:text-claret transition-colors" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && editing && (
        <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-ink/60 backdrop-blur-sm py-10 px-4">
          <div className="bg-paper w-full max-w-2xl relative">
            <div className="flex items-center justify-between px-8 py-5 border-b border-ink/10 sticky top-0 bg-paper">
              <h2 className="font-serif text-2xl font-semibold">{editing.id ? "Edit place" : "Add a place"}</h2>
              <button onClick={() => setShowForm(false)} aria-label="Close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-8 space-y-5">
              <div className="grid grid-cols-[1.4fr_1fr] gap-4">
                <Field label="Name"><input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="admin-input" /></Field>
                <Field label="Page URL (slug)">
                  <div className="flex items-center border border-ink/18 focus-within:border-claret">
                    <span className="pl-3 pr-1 text-sm text-ink-soft/70 whitespace-nowrap">/place/</span>
                    <input
                      value={editing.slug || ""}
                      onChange={(e) => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-") })}
                      className="flex-1 bg-white py-[0.65rem] pr-3 text-sm outline-none"
                      placeholder="sweet-loretta-bar"
                    />
                  </div>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Category">
                  <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="admin-input">
                    {CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Country">
                  <select value={editing.country} onChange={(e) => setEditing({ ...editing, country: e.target.value })} className="admin-input">
                    {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Region / State"><input value={editing.region} onChange={(e) => setEditing({ ...editing, region: e.target.value })} className="admin-input" placeholder="Alberta" /></Field>
                <Field label="City"><input value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })} className="admin-input" placeholder="Calgary" /></Field>
                <Field label="Neighbourhood"><input value={editing.neighborhood} onChange={(e) => setEditing({ ...editing, neighborhood: e.target.value })} className="admin-input" placeholder="Beltline" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Cuisine / type"><input value={editing.cuisine} onChange={(e) => setEditing({ ...editing, cuisine: e.target.value })} className="admin-input" /></Field>
                <Field label="Price">
                  <select value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="admin-input">
                    {["$", "$$", "$$$", "$$$$"].map((c) => <option key={c}>{c}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Image URL"><input value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} className="admin-input" placeholder="https://…" /></Field>
              <Field label="Summary / excerpt (shown on cards)"><textarea rows={2} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className="admin-input resize-none" /></Field>
              <Field label="Full review (one paragraph per line — leave a blank line between paragraphs)">
                <textarea
                  rows={10}
                  value={(editing.body || []).join("\n\n")}
                  onChange={(e) => setEditing({ ...editing, body: e.target.value.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean) })}
                  className="admin-input leading-relaxed"
                  placeholder="Write the full Placeworthy review here…"
                />
              </Field>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Reviewer"><input value={editing.author || ""} onChange={(e) => setEditing({ ...editing, author: e.target.value })} className="admin-input" placeholder="M. Delacroix" /></Field>
                <Field label="Date"><input value={editing.date || ""} onChange={(e) => setEditing({ ...editing, date: e.target.value })} className="admin-input" placeholder="Jul 2025" /></Field>
                <Field label="Tags (comma-separated)"><input value={(editing.tags || []).join(", ")} onChange={(e) => setEditing({ ...editing, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} className="admin-input" placeholder="Cocktails, Late Night" /></Field>
              </div>
              <div>
                <span className="eyebrow text-ink-soft text-[10px]">Accolades</span>
                <div className="flex flex-wrap gap-2 mt-3">
                  {ACCOLADES.map((a) => {
                    const on = (editing.accolades || []).includes(a);
                    return (
                      <button
                        type="button"
                        key={a}
                        onClick={() => setEditing({
                          ...editing,
                          accolades: on
                            ? editing.accolades.filter((x) => x !== a)
                            : [...(editing.accolades || []), a],
                        })}
                        className={`px-3 py-1.5 text-[12px] font-semibold tracking-wide transition-colors ${on ? "bg-brass text-ink" : "bg-ink/5 text-ink-soft hover:bg-ink/10"}`}
                      >
                        {a}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <span className="eyebrow text-ink-soft text-[10px]">Scores (1–10)</span>
                <div className="grid grid-cols-5 gap-3 mt-3">
                  {CRITERIA.map((c) => (
                    <div key={c.key}>
                      <label className="text-[11px] text-ink-soft block mb-1">{c.label}</label>
                      <input type="number" min="1" max="10" value={editing.scores[c.key]} onChange={(e) => setEditing({ ...editing, scores: { ...editing.scores, [c.key]: Number(e.target.value) } })} className="admin-input text-center" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-6 pt-2">
                <label className="block">
                  <span className="eyebrow text-ink-soft text-[10px] block mb-1.5">Distinction</span>
                  <select value={editing.distinction} onChange={(e) => setEditing({ ...editing, distinction: Number(e.target.value) })} className="admin-input">
                    <option value={0}>None (reviewed)</option>
                    <option value={1}>◆ Placeworthy — Recommended</option>
                    <option value={2}>◆◆ Exceptional — Outstanding</option>
                    <option value={3}>◆◆◆ Destination — Worth the trip</option>
                  </select>
                </label>
                <label className="flex items-center gap-2 cursor-pointer pt-5">
                  <input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} />
                  <span className="text-sm font-medium">Featured</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer pt-5">
                  <input type="checkbox" checked={editing.visible} onChange={(e) => setEditing({ ...editing, visible: e.target.checked })} />
                  <span className="text-sm font-medium">Visible to visitors</span>
                </label>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-ink/10 sticky bottom-0 bg-paper">
              <button onClick={() => setShowForm(false)} className="px-5 py-2.5 text-sm font-semibold text-ink-soft hover:text-ink">Cancel</button>
              <button onClick={save} disabled={!editing.name} className="px-6 py-2.5 bg-claret text-paper text-sm font-semibold hover:bg-claret-dark disabled:opacity-40 transition-colors">Save place</button>
            </div>
          </div>
        </div>
      )}

      {showGuideForm && editingGuide && (
        <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-ink/60 backdrop-blur-sm py-10 px-4">
          <div className="bg-paper w-full max-w-2xl relative" data-testid="guide-form">
            <div className="flex items-center justify-between px-8 py-5 border-b border-ink/10 sticky top-0 bg-paper z-10">
              <h2 className="font-serif text-2xl font-semibold">{editingGuide.id ? "Edit guide" : "Add a guide"}</h2>
              <button onClick={() => setShowGuideForm(false)} aria-label="Close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-8 space-y-5">
              <div className="grid grid-cols-[1.4fr_1fr] gap-4">
                <Field label="Title"><input value={editingGuide.title} onChange={(e) => setEditingGuide({ ...editingGuide, title: e.target.value })} data-testid="guide-title-input" className="admin-input" placeholder="The Calgary Date-Night Ten" /></Field>
                <Field label="Page URL (slug)">
                  <div className="flex items-center border border-ink/18 focus-within:border-claret">
                    <span className="pl-3 pr-1 text-sm text-ink-soft/70 whitespace-nowrap">/guide/</span>
                    <input
                      value={editingGuide.slug || ""}
                      onChange={(e) => setEditingGuide({ ...editingGuide, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-") })}
                      className="flex-1 bg-white py-[0.65rem] pr-3 text-sm outline-none"
                      placeholder="calgary-date-night"
                    />
                  </div>
                </Field>
              </div>
              <Field label="Cover image URL"><input value={editingGuide.cover} onChange={(e) => setEditingGuide({ ...editingGuide, cover: e.target.value })} className="admin-input" placeholder="https://…" /></Field>
              <Field label="Blurb / description"><textarea rows={3} value={editingGuide.dek} onChange={(e) => setEditingGuide({ ...editingGuide, dek: e.target.value })} className="admin-input resize-none" placeholder="What this guide is about…" /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Author"><input value={editingGuide.author} onChange={(e) => setEditingGuide({ ...editingGuide, author: e.target.value })} className="admin-input" placeholder="The Placeworthy Editors" /></Field>
                <Field label="Date"><input value={editingGuide.date} onChange={(e) => setEditingGuide({ ...editingGuide, date: e.target.value })} className="admin-input" placeholder="Jul 2025" /></Field>
              </div>
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="eyebrow text-ink-soft text-[10px]">Places in this guide</span>
                  <span className="text-xs text-ink-soft">{(editingGuide.placeSlugs || []).length} selected</span>
                </div>
                <div className="max-h-64 overflow-y-auto border border-ink/12 divide-y divide-ink/8" data-testid="guide-places-select">
                  {places.map((p) => {
                    const on = (editingGuide.placeSlugs || []).includes(p.slug);
                    return (
                      <label key={p.id} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-ink/[0.03]">
                        <input type="checkbox" checked={on} onChange={() => togglePlaceInGuide(p.slug)} data-testid={`guide-place-${p.slug}`} />
                        <span className="w-9 h-9 bg-ink/5 overflow-hidden shrink-0">
                          {p.image ? <img src={p.image} alt="" className="w-full h-full object-cover" /> : null}
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-medium truncate">{p.name}</span>
                          <span className="block text-xs text-ink-soft truncate">{p.city}, {p.country}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editingGuide.visible} onChange={(e) => setEditingGuide({ ...editingGuide, visible: e.target.checked })} data-testid="guide-visible-toggle" />
                <span className="text-sm font-medium">Visible to visitors</span>
              </label>
            </div>
            <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-ink/10 sticky bottom-0 bg-paper">
              <button onClick={() => setShowGuideForm(false)} className="px-5 py-2.5 text-sm font-semibold text-ink-soft hover:text-ink">Cancel</button>
              <button onClick={saveGuide} disabled={!editingGuide.title} data-testid="save-guide-btn" className="px-6 py-2.5 bg-claret text-paper text-sm font-semibold hover:bg-claret-dark disabled:opacity-40 transition-colors">Save guide</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="eyebrow text-ink-soft text-[10px] block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
